'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { CommonProps } from '../types.js';
import type { BaseTypographyProps, TextStyleOptions } from './types.js';
import { textStyle } from './utils.js';

export interface OrderedListProps extends BaseTypographyProps, Omit<TextStyleOptions, 'alignment'> {
  /** An integer to start numbering list items from. */
  startFrom?: number;
}

function OrderedList(
  {
    children,
    startFrom,
    UNSAFE_className,
    UNSAFE_style,
    variant,
    fontStyle,
    textDecoration,
    tone,
    ...props
  }: OrderedListProps,
  ref: ForwardedRef<HTMLOListElement>,
) {
  return (
    <ol
      {...props}
      ref={ref}
      className={clsx(
        'cim-list',
        textStyle({ variant, fontStyle, alignment: 'start', textDecoration, tone }),
        UNSAFE_className,
      )}
      style={UNSAFE_style}
      start={startFrom}
    >
      {children}
    </ol>
  );
}

/**
 * Renders an ordered list of text items in a specified style.
 *
 * See [list usage guidelines](https://ui.cimpress.io/components/list/).
 */
const _OrderedList = withStyleProps(forwardRef(OrderedList), 'OrderedList');

export interface UnorderedListProps extends BaseTypographyProps, Omit<TextStyleOptions, 'alignment'> {}

function UnorderedList(
  { children, UNSAFE_className, UNSAFE_style, variant, fontStyle, textDecoration, tone, ...props }: UnorderedListProps,
  ref: ForwardedRef<HTMLUListElement>,
) {
  return (
    <ul
      {...props}
      ref={ref}
      className={clsx(
        'cim-list',
        textStyle({ variant, fontStyle, alignment: 'start', textDecoration, tone }),
        UNSAFE_className,
      )}
      style={UNSAFE_style}
    >
      {children}
    </ul>
  );
}

/**
 * Renders an unordered list of text items in a specified style.
 *
 * See [list usage guidelines](https://ui.cimpress.io/components/list/).
 */
const _UnorderedList = withStyleProps(forwardRef(UnorderedList), 'UnorderedList');

export { _OrderedList as OrderedList, _UnorderedList as UnorderedList };

export interface ListItemProps extends CommonProps {
  children: ReactNode;
}

/** Renders a list item within a list. */
export function ListItem({ children, UNSAFE_className, UNSAFE_style, ...props }: ListItemProps) {
  return (
    <li {...props} className={clsx('cim-list-item', UNSAFE_className)} style={UNSAFE_style}>
      {children}
    </li>
  );
}

ListItem.displayName = 'ListItem';
